import React, { useState, useRef } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { db, storage } from '../firebase';
import html2canvas from 'html2canvas'; // You'll need to install this: npm install html2canvas

export default function AnalyticsTab({ dealerAppsCode, clientName, clientId }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // eslint-disable-next-line
  const [uploadingCSV, setUploadingCSV] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const reportRef = useRef(null);

  const calculateResponseRate = (responses, appointments) => {
    const resp = parseInt(responses || '0');
    const appt = parseInt(appointments || '0');
    if (resp === 0) return '0%';
    return ((appt / resp) * 100).toFixed(1) + '%';
  };

  const refreshAnalytics = async () => {
    if (!dealerAppsCode) {
      setError("No DealerApps code available for this client.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const functions = getFunctions();
      const getDealerAnalytics = httpsCallable(functions, "getDealerAnalytics");
      const result = await getDealerAnalytics({ dealerCode: dealerAppsCode });
      
      // Calculate response rates for each blast if missing
      if (result.data.blasts) {
        result.data.blasts = result.data.blasts.map(blast => ({
          ...blast,
          responseRate: blast.responseRate || calculateResponseRate(blast.responses, blast.appointments)
        }));
      }
      
      console.log("Processed analytics data:", result.data);
      setAnalytics(result.data);
      
      // Automatically generate and save both CSV and image report
      await generateAndSaveReports(result.data, "Analytics data refreshed");
      
    } catch (err) {
      console.error("Analytics error:", err.code, err.message, err.details);
      setError(err.message || "Unknown analytics error");
    } finally {
      setLoading(false);
    }
  };

  const generateCSV = (analyticsData) => {
    if (!analyticsData) return null;

    const currentDate = new Date().toLocaleDateString();
    const headers = [
      'Date Generated',
      'Client Name',
      'DealerApps Code',
      'Daily Responses',
      'Campaign Title',
      'Invitations',
      'Responses',
      'Appointments',
      'Response Rate',
      'Sale Start Date',
      'Sale End Date',
      'Lead URL',
      'Report URL'
    ];

    const rows = [];
    
    // Add a summary row with daily response
    rows.push([
      currentDate,
      clientName,
      dealerAppsCode,
      analyticsData.dailyResponse || '0',
      'DAILY TOTAL',
      '',
      analyticsData.dailyResponse || '0',
      '',
      '',
      '',
      '',
      '',
      ''
    ]);

    // Add rows for each blast/campaign
    analyticsData.blasts.forEach(blast => {
      rows.push([
        currentDate,
        clientName,
        dealerAppsCode,
        analyticsData.dailyResponse || '0',
        blast.title || '',
        blast.invitations || '0',
        blast.responses || '0',
        blast.appointments || '0',
        blast.responseRate || '',
        blast.saleDates?.[0] || '',
        blast.saleDates?.[1] || '',
        blast.leadUrl || '',
        blast.reportUrl || ''
      ]);
    });

    // Generate CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => 
        row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
      )
    ].join('\r\n');

    return csvContent;
  };

  const generateHTMLReport = (analyticsData) => {
    if (!analyticsData) return null;

    const currentDate = new Date().toLocaleDateString();
    const totalResponses = analyticsData.blasts?.reduce((sum, blast) => 
      sum + parseInt(blast.responses || '0'), 0) || 0;
    const totalAppointments = analyticsData.blasts?.reduce((sum, blast) => 
      sum + parseInt(blast.appointments || '0'), 0) || 0;
    const avgResponseRate = analyticsData.blasts?.length > 0 ? 
      (analyticsData.blasts.reduce((sum, blast) => {
        // Extract numeric value from responseRate string (e.g., "16.8%" -> 16.8)
        const rate = parseFloat((blast.responseRate || '0').replace('%', '')) || 0;
        return sum + rate;
      }, 0) / analyticsData.blasts.length).toFixed(1) + '%' : '0%';

    return `
      <!DOCTYPE html>
      <html lang="en">
      <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>${clientName} Campaign Analytics</title>
          <style>
              @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;600;700;800&display=swap');
              
              * {
                  margin: 0;
                  padding: 0;
                  box-sizing: border-box;
              }
              
              body {
                  font-family: 'Inter', sans-serif;
                  background: linear-gradient(135deg, #01a2ff 0%, #001aff 100%);
                  min-height: 100vh;
                  padding: 20px;
                  display: flex;
                  justify-content: center;
                  align-items: center;
              }
              
              .infographic {
                  background: white;
                  border-radius: 24px;
                  box-shadow: 0 32px 64px rgba(0, 0, 0, 0.2);
                  padding: 40px;
                  max-width: 1200px;
                  width: 100%;
                  position: relative;
                  overflow: hidden;
              }
              
              .infographic::before {
                  content: '';
                  position: absolute;
                  top: 0;
                  left: 0;
                  right: 0;
                  height: 8px;
                  background: linear-gradient(135deg, #01a2ff 0%, #001aff 100%);
              }
              
              .header {
                  text-align: center;
                  margin-bottom: 40px;
              }
              
              .header h1 {
                  font-size: 2.5rem;
                  font-weight: 800;
                  color: #2c3e50;
                  margin-bottom: 8px;
                  background: linear-gradient(135deg, #01a2ff 0%, #001aff 100%);
                  -webkit-background-clip: text;
                  -webkit-text-fill-color: transparent;
                  background-clip: text;
              }
              
              .header p {
                  font-size: 1.1rem;
                  color: #7f8c8d;
                  font-weight: 400;
              }
              
              .date-badge {
                  display: inline-block;
                  background: linear-gradient(135deg, #01a2ff 0%, #001aff 100%);
                  color: white;
                  padding: 8px 20px;
                  border-radius: 20px;
                  font-size: 0.9rem;
                  font-weight: 600;
                  margin-top: 10px;
              }
              
              .summary-cards {
                  display: grid;
                  grid-template-columns: repeat(3, 1fr);
                  gap: 24px;
                  margin-bottom: 40px;
              }
              
              .summary-card {
                  background: linear-gradient(135deg, #f8f9fa, #e9ecef);
                  border-radius: 16px;
                  padding: 24px;
                  text-align: center;
                  border: 2px solid transparent;
                  transition: all 0.3s ease;
                  position: relative;
                  overflow: hidden;
              }
              
              .summary-card::before {
                  content: '';
                  position: absolute;
                  top: 0;
                  left: 0;
                  right: 0;
                  height: 4px;
                  background: var(--accent-color);
              }
              
              .summary-card.responses::before { background: #ff0000; }
              .summary-card.appointments::before { background: #048500; }
              .summary-card.rate::before { background: #001aff; }
              
              .card-number {
                  font-size: 2.5rem;
                  font-weight: 800;
                  margin-bottom: 8px;
              }
              
              .responses .card-number { color: #e20000; }
              .appointments .card-number { color: #048500; }
              .rate .card-number { color: #001aff; }
              
              .card-label {
                  font-size: 1rem;
                  color: #6c757d;
                  font-weight: 600;
                  text-transform: uppercase;
                  letter-spacing: 0.5px;
              }
              
              .campaigns-section {
                  margin-bottom: 40px;
              }
              
              .section-title {
                  font-size: 1.8rem;
                  font-weight: 700;
                  color: #2c3e50;
                  margin-bottom: 24px;
                  text-align: center;
              }
              
              .campaigns-grid {
                  display: grid;
                  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
                  gap: 20px;
              }
              
              .campaign-card {
                  background: white;
                  border-radius: 16px;
                  padding: 24px;
                  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
                  border: 2px solid #f1f3f4;
                  position: relative;
              }
              
              .campaign-header {
                  display: flex;
                  justify-content: space-between;
                  align-items: flex-start;
                  margin-bottom: 16px;
              }
              
              .campaign-title {
                  font-size: 1.1rem;
                  font-weight: 700;
                  color: #2c3e50;
                  flex: 1;
              }
              
              .campaign-duration {
                  font-size: 0.85rem;
                  color: #6c757d;
                  background: #f8f9fa;
                  padding: 4px 10px;
                  border-radius: 12px;
                  white-space: nowrap;
                  margin-left: 12px;
              }
              
              .campaign-metrics {
                  display: flex;
                  justify-content: space-between;
                  gap: 16px;
              }
              
              .metric {
                  flex: 1;
                  text-align: center;
              }
              
              .metric-value {
                  font-size: 1.8rem;
                  font-weight: 800;
                  margin-bottom: 4px;
              }
              
              .metric-label {
                  font-size: 0.85rem;
                  color: #6c757d;
                  font-weight: 600;
                  text-transform: uppercase;
                  letter-spacing: 0.3px;
              }
              
              .responses-metric .metric-value { color: #e20000; }
              .appointments-metric .metric-value { color: #048500; }
              .rate-metric .metric-value { color: #001aff; }
              
              .progress-bar {
                  width: 100%;
                  height: 6px;
                  background: #e9ecef;
                  border-radius: 3px;
                  overflow: hidden;
                  margin-top: 16px;
              }
              
              .progress-fill {
                  height: 100%;
                  background: linear-gradient(135deg, #01a2ff 0%, #001aff 100%);
                  border-radius: 3px;
              }
              
              .footer {
                  text-align: center;
                  margin-top: 32px;
                  padding-top: 24px;
                  border-top: 2px solid #e9ecef;
              }
              
              .footer p {
                  color: #6c757d;
                  font-size: 0.9rem;
              }
          </style>
      </head>
      <body>
          <div class="infographic">
              <div class="header">
                  <h1>${clientName}</h1>
                  <p>Facebook Campaign Performance Analytics</p>
                  <div class="date-badge">Report Generated: ${currentDate}</div>
              </div>
              
              <div class="summary-cards">
                  <div class="summary-card responses">
                      <div class="card-number">${totalResponses}</div>
                      <div class="card-label">Total Responses</div>
                  </div>
                  <div class="summary-card appointments">
                      <div class="card-number">${totalAppointments}</div>
                      <div class="card-label">Appointments</div>
                  </div>
                  <div class="summary-card rate">
                      <div class="card-number">${avgResponseRate}</div>
                      <div class="card-label">Avg Response Rate</div>
                  </div>
              </div>
              
              <div class="campaigns-section">
                  <h2 class="section-title">Campaign Performance Breakdown</h2>
                  <div class="campaigns-grid">
                      ${analyticsData.blasts.map(blast => `
                          <div class="campaign-card">
                              <div class="campaign-header">
                                  <div class="campaign-title">${blast.title || 'Untitled Campaign'}</div>
                                  <div class="campaign-duration">${blast.saleDates ? blast.saleDates.join(' - ') : 'N/A'}</div>
                              </div>
                              <div class="campaign-metrics">
                                  <div class="metric responses-metric">
                                      <div class="metric-value">${blast.responses || '0'}</div>
                                      <div class="metric-label">Responses</div>
                                  </div>
                                  <div class="metric appointments-metric">
                                      <div class="metric-value">${blast.appointments || '0'}</div>
                                      <div class="metric-label">Appointments</div>
                                  </div>
                                  <div class="metric rate-metric">
                                      <div class="metric-value">${blast.responseRate || '0%'}</div>
                                      <div class="metric-label">Rate</div>
                                  </div>
                              </div>
                              <div class="progress-bar">
                                  <div class="progress-fill" style="width: ${Math.max(parseFloat((blast.responseRate || '0').replace('%', '')) || 0, 0)}%"></div>
                              </div>
                          </div>
                      `).join('')}
                  </div>
              </div>
              
              <div class="footer">
                  <p>Analytics powered by DealerApps • Code: ${dealerAppsCode}</p>
              </div>
          </div>
      </body>
      </html>
    `;
  };

  const generateAndSaveReports = async (analyticsData, actionType = "Manual report generation") => {
    if (!analyticsData || !clientId) return;

    setGeneratingReport(true);
    try {
      const currentDate = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
      const currentTime = new Date().toTimeString().split(' ')[0].replace(/:/g, '-'); // HH-MM-SS
      
      // Generate CSV
      const csvContent = generateCSV(analyticsData);
      const csvFileName = `${clientName}_Analytics_${currentDate}_${currentTime}.csv`;
      const csvBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      
      // Generate HTML Report (for image generation only)
      const htmlContent = generateHTMLReport(analyticsData);

      // Create temporary HTML element for html2canvas
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlContent;
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.width = '1200px';
      document.body.appendChild(tempDiv);

      // Wait for content to render
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Generate image from HTML with better height calculation
      const infographicElement = tempDiv.querySelector('.infographic');
      const actualHeight = infographicElement.scrollHeight;
      
      const canvas = await html2canvas(infographicElement, {
        width: 1200,
        height: actualHeight,
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        scrollX: 0,
        scrollY: 0
      });

      // Clean up temporary element
      document.body.removeChild(tempDiv);

      // Convert canvas to blob
      const imageBlob = await new Promise(resolve => 
        canvas.toBlob(resolve, 'image/png', 0.95)
      );

      const imageFileName = `${clientName}_Analytics_Report_${currentDate}_${currentTime}.png`;

      // Upload only CSV and image files to Firebase Storage
      const csvFileRef = ref(storage, `analytics/${clientId}/${csvFileName}`);
      const imageFileRef = ref(storage, `analytics/${clientId}/${imageFileName}`);

      const [csvUrl, imageUrl] = await Promise.all([
        uploadBytes(csvFileRef, csvBlob).then(snapshot => getDownloadURL(snapshot.ref)),
        uploadBytes(imageFileRef, imageBlob).then(snapshot => getDownloadURL(snapshot.ref))
      ]);

      // Create summary of analytics data
      const summary = {
        dailyResponses: analyticsData.dailyResponse || '0',
        totalCampaigns: analyticsData.blasts?.length || 0,
        totalResponses: analyticsData.blasts?.reduce((sum, blast) => 
          sum + parseInt(blast.responses || '0'), 0) || 0,
        totalAppointments: analyticsData.blasts?.reduce((sum, blast) => 
          sum + parseInt(blast.appointments || '0'), 0) || 0
      };

      // Save two separate messages - CSV and image
      await saveSeparateMessages(actionType, summary, {
        csv: { url: csvUrl, name: csvFileName },
        image: { url: imageUrl, name: imageFileName }
      });

      console.log(`Analytics reports saved to messages: ${csvFileName}, ${imageFileName}`);

    } catch (err) {
      console.error('Error generating and saving reports:', err);
      setError('Failed to generate reports');
    } finally {
      setGeneratingReport(false);
    }
  };

  const generateAndSaveCSV = async (analyticsData, actionType = "Manual CSV download") => {
    if (!analyticsData || !clientId) return;

    setUploadingCSV(true);
    try {
      const csvContent = generateCSV(analyticsData);
      const currentDate = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
      const currentTime = new Date().toTimeString().split(' ')[0].replace(/:/g, '-'); // HH-MM-SS
      const fileName = `${clientName}_Analytics_${currentDate}_${currentTime}.csv`;

      // Convert CSV to blob
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      
      // Upload to Firebase Storage
      const fileRef = ref(storage, `analytics/${clientId}/${fileName}`);
      await uploadBytes(fileRef, blob);
      const downloadURL = await getDownloadURL(fileRef);

      // Create summary of analytics data
      const summary = {
        dailyResponses: analyticsData.dailyResponse || '0',
        totalCampaigns: analyticsData.blasts?.length || 0,
        totalResponses: analyticsData.blasts?.reduce((sum, blast) => 
          sum + parseInt(blast.responses || '0'), 0) || 0,
        totalAppointments: analyticsData.blasts?.reduce((sum, blast) => 
          sum + parseInt(blast.appointments || '0'), 0) || 0
      };

      const messageText = `${actionType} - CSV Generated\n\nSummary:\n• Daily Responses: ${summary.dailyResponses}\n• Total Campaigns: ${summary.totalCampaigns}\n• Total Responses: ${summary.totalResponses}\n• Total Appointments: ${summary.totalAppointments}\n\nClick the attachment below to download the full analytics report.`;

      // Save message with CSV attachment
      await saveMessageWithAttachment(messageText, downloadURL, fileName, summary);

      console.log(`CSV saved to messages: ${fileName}`);

    } catch (err) {
      console.error('Error generating and saving CSV:', err);
      setError('Failed to save CSV to messages');
    } finally {
      setUploadingCSV(false);
    }
  };

  const saveMessageWithAttachment = async (messageText, attachmentUrl, fileName, analyticsData) => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) return;

      // Add message to the client's message collection
      const msgsRef = collection(db, 'clients', clientId, 'messages');
      await addDoc(msgsRef, {
        text: messageText,
        senderId: user.uid,
        senderName: user.displayName || 'System',
        senderRole: 'admin',
        recipientType: 'internal',
        createdAt: serverTimestamp(),
        read: false,
        analyticsAction: true,
        analyticsData: analyticsData,
        attachmentUrl: attachmentUrl,
        attachmentName: fileName,
        attachmentType: fileName.endsWith('.csv') ? 'text/csv' : 'text/html'
      });

    } catch (err) {
      console.error('Error saving message with attachment:', err);
    }
  };

  const saveSeparateMessages = async (actionType, summary, attachments) => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) return;

      const msgsRef = collection(db, 'clients', clientId, 'messages');

      // Save separate messages for CSV and image attachments only
      await addDoc(msgsRef, {
        text: '📊 CSV Data Export - Raw analytics data in spreadsheet format',
        senderId: user.uid,
        senderName: user.displayName || 'System',
        senderRole: 'admin',
        recipientType: 'internal',
        createdAt: serverTimestamp(),
        read: false,
        analyticsAction: true,
        attachmentUrl: attachments.csv.url,
        attachmentName: attachments.csv.name,
        attachmentType: 'text/csv'
      });

      await addDoc(msgsRef, {
        text: '🖼️ Visual Report Image - Professional analytics infographic for sharing',
        senderId: user.uid,
        senderName: user.displayName || 'System',
        senderRole: 'admin',
        recipientType: 'internal',
        createdAt: serverTimestamp(),
        read: false,
        analyticsAction: true,
        attachmentUrl: attachments.image.url,
        attachmentName: attachments.image.name,
        attachmentType: 'image/png'
      });

    } catch (err) {
      console.error('Error saving separate messages:', err);
    }
  };

  const downloadCSVManually = async () => {
    if (!analytics) {
      alert("No analytics data available to download");
      return;
    }

    try {
      const csvContent = generateCSV(analytics);
      const currentDate = new Date().toISOString().split('T')[0];
      const fileName = `${clientName}_Analytics_${currentDate}.csv`;

      // Create and download CSV file locally
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // Also save to messages
      await generateAndSaveCSV(analytics, "Manual CSV download");

    } catch (err) {
      console.error('Error downloading CSV:', err);
      alert('Failed to download CSV');
    }
  };

  // eslint-disable-next-line
  const generateFullReport = async () => {
    if (!analytics) {
      alert("No analytics data available to generate report");
      return;
    }

    await generateAndSaveReports(analytics, "Full analytics report generation");
  };

  return (
    <div>
      <div className="client-actions" style={{ 
        marginBottom: 8, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'wrap'
      }}>
        <button 
          onClick={refreshAnalytics} 
          disabled={loading || generatingReport}
          style={{
            opacity: (loading || generatingReport) ? 0.6 : 1
          }}
        >
          {loading ? "Refreshing & Generating Reports..." : "Refresh Analytics"}
        </button>
        
        {analytics && (
          <button 
            onClick={downloadCSVManually}
            disabled={loading || generatingReport}
            style={{
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              padding: '6px 12px',
              borderRadius: '4px',
              cursor: (loading || generatingReport) ? 'not-allowed' : 'pointer',
              opacity: (loading || generatingReport) ? 0.6 : 1
            }}
          >
            Download CSV Only
          </button>
        )}
      </div>

      {(loading || generatingReport) && (
        <div style={{ 
          backgroundColor: '#d4edda', 
          border: '1px solid #c3e6cb',
          padding: '8px 12px', 
          borderRadius: '4px', 
          marginBottom: '12px',
          fontSize: '14px',
          color: '#155724'
        }}>
          {loading && '📊 Refreshing analytics and generating reports (CSV + Image)...'}
        </div>
      )}
      
      {error && <p style={{ color: "red" }}>{error}</p>}

      {analytics && (
        <div style={{ marginTop: "1rem" }}>
          {/* DAILY CHART */}
          <div style={{ 
            backgroundColor: '#f8f9fa', 
            padding: '12px', 
            borderRadius: '6px', 
            marginBottom: '1rem',
            border: '1px solid #dee2e6'
          }}>
            <p><strong>📈 Daily Responses:</strong> {analytics.dailyResponse}</p>
            <small style={{ color: '#6c757d' }}>
              CSV and image report automatically saved to messages when refreshed
            </small>
          </div>

          {/* BLAST CARDS */}
          {analytics.blasts.map((b, i) => (
            <div
              key={i}
              style={{
                border: "1px solid #ddd",
                borderRadius: 6,
                padding: "1rem",
                marginTop: "1rem",
                backgroundColor: '#fafafa'
              }}
            >
              <h6 style={{ marginBottom: '0.5rem', color: '#343a40' }}>{b.title}</h6>

              <div style={{ marginBottom: '0.5rem' }}>
                {/* Only show Invitations if non-zero */}
                {b.invitations !== "0" && <p style={{ margin: '4px 0' }}>📧 Invitations: {b.invitations}</p>}
                <p style={{ margin: '4px 0' }}>📊 Responses: {b.responses}</p>
                {b.appointments && b.appointments !== "0" && (
                  <p style={{ margin: '4px 0' }}>📅 Appointments: {b.appointments}</p>
                )}
                {b.responseRate && <p style={{ margin: '4px 0' }}>📈 Response Rate: {b.responseRate}</p>}
                <p style={{ margin: '4px 0' }}>📅 Sale Dates: {b.saleDates.join(" → ")}</p>
              </div>

              <div style={{ marginTop: "0.75rem" }}>
                <a
                  href={b.leadUrl}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: "inline-block",
                    marginRight: 8,
                    marginBottom: 4,
                    padding: "0.5rem 1rem",
                    background: "#2600ff",
                    color: "white",
                    borderRadius: 4,
                    textDecoration: "none",
                    fontSize: '14px'
                  }}
                >
                  📋 Leads
                </a>

                {b.reportUrl && (
                  <a
                    href={b.reportUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      display: "inline-block",
                      padding: "0.5rem 1rem",
                      background: "#2600ff",
                      color: "white",
                      borderRadius: 4,
                      textDecoration: "none",
                      fontSize: '14px'
                    }}
                  >
                    📊 Reports
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Hidden element for report generation */}
      <div ref={reportRef} style={{ display: 'none' }} />
    </div>
  );
}